<template>
  <v-container>
    <v-overlay :value="overlay">
      <v-progress-circular
        :size="50"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </v-overlay>
    <h4>Inputs for this type of export:</h4>
    <v-container>
    <v-col class="elevation-1"
      v-for="input in inputsInternal" :key="input.prop"
    >
      <v-text-field v-if="input.type === 'fiscalYear'"
        class="tmc-export fiscalYear"
        v-model.number="input.value"
        :label="input.label || 'Fiscal Year'"
        type="number"
      ></v-text-field>
      <ClarionDateControl v-else-if="input.type === 'date'"
        v-model="input.value"
        :isRequired="true"
        :isIsoDate="true"
        :label="input.label || 'Date'"
        classes="tmc-export date"
      />
      <v-row v-else-if="input.type === 'monthAndYear'">
        <v-col cols="2">
          <v-select
            :disabled="isDisableMonthAndYearFields"
            v-model="input.selectedMonth"
            :items="months"
            :label="'Month'"
            class="tmc-export month"
          />
        </v-col>
        <v-col cols="2">
          <v-text-field
            :disabled="isDisableMonthAndYearFields"
            v-model.number="input.selectedYear"
            label="Year"
            type="number"
            class="tmc-export year"
          ></v-text-field>
        </v-col>
        <v-col cols="1"></v-col>
        <v-col cols="3">
          <v-checkbox
            class="font-weight-bold"
            v-model="isExportForEntireFy"
            label="Export for entire FY"
          ></v-checkbox>
        </v-col>
      </v-row>
      <v-row v-else-if="input.type === 'showFromToDates'">
        <v-col id="tmc-export-from-date">
          <ClarionDateControl
            :isDisabled="isDisableFromToDatesFields"
            v-model="input.fromDate"
            :isRequired="true"
            :isIsoDate="true"
            label="From Date" />
        </v-col>
          <v-col>
            <ClarionDateControl
              :isDisabled="isDisableFromToDatesFields"
              v-model="input.toDate"
              :isRequired="true"
              :isIsoDate="true"
              label="To Date" />
        </v-col>
        <v-col>
          <v-checkbox
            class="font-weight-bold"
            v-model="isExportForEntireFyWithFromToDates"
            label="Export for entire FY"
          ></v-checkbox>
        </v-col>
      </v-row>
      <v-row v-else-if="input.type === 'optionalDate'">
        <v-col cols="6">
          <v-checkbox
            class="font-weight-bold"
            v-model="input.enabled"
            :label="input.label || 'Optional end date for report'"
          ></v-checkbox>
        </v-col>
        <v-col cols="6">
          <ClarionDateControl
            :isDisabled="!input.enabled"
            v-model="input.value"
            :isRequired="false"
            :isIsoDate="true"
            label="End / Through Date"
          />
        </v-col>
      </v-row>
      <v-text-field v-else-if="input.type === 'constant'"
        classes="tmc-export readonly text-field"
        v-model="input.value"
        :label="input.label || input.prop"
        type="text"
        readonly
      ></v-text-field>
      <v-checkbox v-else-if="input.type === 'optionalCheckBox'"
        class="font-weight-bold"
        v-model="input.value"
        :label="input.label || 'Optional Value'"
        :disabled="input.disabled"
      ></v-checkbox>
      <v-text-field v-else-if="input.type === 'optionalTextField'"
        v-model="input.value"
        :label="input.label || 'Optional Text Field'"
        :disabled="input.disabled"
      ></v-text-field>
      <span v-else>
        Unknown type: "{{input.type}}"
      </span>
    </v-col>
    <v-col>
      <v-btn ref="btnExport"
        @click="exportButton"
        target="_blank"
        class="mx-6">
        Export
      </v-btn>
      <v-btn v-if="isExportToSftpBtnEnabled"
        @click="exportToSftp"
        target="_blank">
        Export (to the sftp)
      </v-btn>
      <v-btn v-if="isExportToCloudBtnEnabled"
        @click="exportToCloud"
        target="_blank">
        Export (to the cloud)
      </v-btn>
      <!-- TICKET-2778: same file, delivered to the Clerk system's inbox for the clerk to import -->
      <v-btn v-if="isSendToClerkSystemBtnEnabled"
        id="tmc-export-send-to-clerk-system"
        class="mx-2"
        color="primary"
        outlined
        @click="sendToClerkSystem">
        {{ clerkSendRetry ? 'Retry send to Clerk system' : 'Send to Clerk system' }}
      </v-btn>
    </v-col>
    </v-container>
  </v-container>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import ClarionDateControl from '../common/ClarionDateControl.vue';
import { clients } from '../../util/clients';
import {
  destructureDate,
  toFiscalYear,
  toIsoDate,
} from '../../util/shared/tmc-global';
import { checkStringEmptiness } from '../../util/shared/vue-global';
import {
  clerkSendErrorMessage,
  clerkSendSuccessMessage,
  isClerkExportProfile,
  newClientRef,
} from '../../util/shared/treasurer-link';

const { backendRest } = clients.direct;

export const parsedPipelineItem = (i) => {
  const result = {
    ...i,
    sourceTemplate: i.sourceTemplate || '',
    inputs: JSON.parse(i.inputs || '[]'),
    jsonMapper: JSON.parse(i.jsonMapper || '[]'),
    pipeline: JSON.parse(i.pipeline || '[]'),
    csvInputs: JSON.parse(i.csvInputs || '[]'),
    fixedWidthTemplate: JSON.parse(i.fixedWidthTemplate || '[]'),
  };
  return result;
};

export default {
  name: 'ExportFilesBetaInputCols',
  components: {
    ClarionDateControl,
  },
  data: () => ({
    inputsInternal: [],
    token: undefined,
    isExportForEntireFy: false,
    isExportForEntireFyWithFromToDates: false,
    // new flag handled per-input for optional dates
    considerFromAndToDates: false,
    overlay: false,
    // TICKET-2778 "Send to Clerk system"
    clerkLinkConfigured: false,
    // kept across a failed send so Retry reuses it (the Clerk system dedupes on it)
    clerkClientRef: undefined,
    clerkSendRetry: false,
  }),
  props: {
    exportProfileName: {
      type: String,
      default: '',
    },
    program: {
      type: String,
      default: '',
    },
    model: {
      type: String,
      default: '',
    },
    inputs: {
      type: Array,
      default: () => ([]),
    },
    jsonMapper: {
      type: Array,
      default: () => ([]),
    },
    mongoId: {
      type: String,
      default: '',
    },
    fileName: {
      type: String,
      default: '',
    },
    collectionName: {
      type: String,
      default: '',
    },
    fileHeaders: {
      type: Boolean,
      default: true,
    },
  },
  async created() {
    const { jwt } = await this.reAuth();
    this.token = `${jwt}`;
    await this.initializeInputs(this.inputs);
    this.initializeOptionalParams(this.inputs);
    this.emitInputs();
    this.loadClerkLinkConfigured();
  },
  watch: {
    // different inputs = a different file, so it must not reuse the last send's id
    inputsInternal: {
      deep: true,
      handler() {
        this.clerkClientRef = undefined;
        this.clerkSendRetry = false;
      },
    },
  },
  computed: {
    ...mapState({
      warSettingItem: (state) => state.SystemConfig.warSettingItem,
    }),
    months() {
      return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    },
    isDisableMonthAndYearFields() {
      return this.isExportForEntireFy;
    },
    isDisableFromToDatesFields() {
      return this.isExportForEntireFyWithFromToDates && !this.considerFromAndToDates;
    },
    isExportToSftpBtnEnabled() {
      let exportToSftpBtnFlag = false;
      const result = (this.jsonMapper || []).find((items) => items.prop === 'showExportToSftpBtn');
      if (result) {
        exportToSftpBtnFlag = result.value;
      }
      return this.warSettingItem && this.warSettingItem.clerkSystem && exportToSftpBtnFlag;
    },
    isSendToClerkSystemBtnEnabled() {
      return this.clerkLinkConfigured && isClerkExportProfile(this.fileName);
    },
    isExportToCloudBtnEnabled() {
      let exportToCloudBtnFlag = false;
      const result = (this.jsonMapper || []).find((items) => items.prop === 'showExportToCloudBtn');
      if (result) {
        exportToCloudBtnFlag = result.value;
      }
      return exportToCloudBtnFlag;
    },
  },
  methods: {
    ...mapActions({
      reAuth: 'reAuth',
      flashSuccess: 'flashSuccess',
      flashError: 'flashError',
    }),
    async emitInputs() {
      this.$emit('inputs', this.inputsInternal);
    },
    async initializeInputs(inputs) {
      this.inputsInternal = await Promise.all(inputs
        .map(async (i) => {
          switch (i.type) {
            case 'fiscalYear':
              return {
                ...i,
                value: toFiscalYear(new Date()),
              };
            case 'date':
              return {
                ...i,
                value: toIsoDate(),
              };
            case 'showFromToDates':
              return {
                ...i,
                fromDate: toIsoDate(),
                toDate: toIsoDate(),
              };
            case 'optionalDate':
              return {
                ...i,
                value: null,
                enabled: i.enabled || false,
              };
            case 'monthAndYear':
              return {
                ...i,
                selectedMonth: destructureDate(new Date()).month,
                selectedYear: destructureDate(new Date()).year,
              };
            default:
              return i;
          }
        }));
    },
    initializeOptionalParams(inputs) {
      const optionalCheckBoxObj = this.searchObjectInsideArr(inputs, 'considerFromAndToDateRange', 'prop');
      if (optionalCheckBoxObj && optionalCheckBoxObj.prop === 'considerFromAndToDateRange') {
        this.considerFromAndToDates = optionalCheckBoxObj.value;
      } else {
        this.considerFromAndToDates = false;
      }
    },
    searchObjectInsideArr(arrayOfObjects, searchValue, keyName) {
      return (arrayOfObjects || []).find((obj) => obj[keyName] === searchValue);
    },
    exportButton() {
      // validate optional end-date against selected fiscal year
      const validationErr = this.validateOptionalEndDateAgainstFy();
      if (validationErr) {
        this.flashError(validationErr);
        return;
      }
      const url = (this.exportUrl({}) || '').toString();
      if (url && url !== '' && !url.match(/NaN/) && !url.match(/\/$/)) {
        window.open(url, '_blank');
      }
    },
    /* export button for #exportToSftp */
    async exportToSftp() {
      const isExportToLocation = true;
      this.exportToCloudOrSFTP(this.exportUrl({ isExportToLocation }), { isExportToLocation });
    },
    async loadClerkLinkConfigured() {
      try {
        const { data } = await backendRest.get('/treasurer-link/config');
        this.clerkLinkConfigured = !!(data && data.configured);
      } catch (err) {
        // no link (or no access to it): the screen stays exactly as it was
        this.clerkLinkConfigured = false;
      }
    },
    /* TICKET-2778: generate the same file "Export (to the sftp)" would, and put it in the
       Clerk system's inbox. Nothing changes on the Clerk side until the clerk imports it. */
    async sendToClerkSystem() {
      const validationErr = this.validateOptionalEndDateAgainstFy();
      if (validationErr) {
        this.flashError(validationErr);
        return;
      }
      if (!this.clerkClientRef) {
        this.clerkClientRef = newClientRef();
      }
      const baseUrl = (this.exportUrl({ isExportToLocation: true }) || '').toString();
      if (!baseUrl || baseUrl.match(/NaN/)) {
        return;
      }
      const url = `${baseUrl}&isExportToClerkSystem=true&clientRef=${encodeURIComponent(this.clerkClientRef)}`;
      this.overlay = true;
      let status = 0;
      let body = {};
      try {
        const response = await fetch(url);
        status = response.status;
        body = await response.json().catch(() => ({}));
      } catch (err) {
        console.error(err);
        body = { error: 'the server could not be reached', retryable: true };
      }
      this.overlay = false;
      if (status === 200) {
        this.flashSuccess(clerkSendSuccessMessage(body.data, this.exportProfileName));
        this.clerkClientRef = undefined;
        this.clerkSendRetry = false;
        this.$emit('sentToClerkSystem', body.data);
        return;
      }
      this.flashError(clerkSendErrorMessage(body));
      // a rejected file (bad data, not configured) is not retried with the same id
      if (!body.retryable) {
        this.clerkClientRef = undefined;
      }
      this.clerkSendRetry = !!this.clerkClientRef;
    },
    /* export button for #exportToCloud */
    async exportToCloud() {
      const isExportToCloud = true;
      this.exportToCloudOrSFTP(this.exportUrl({ isExportToCloud }), { isExportToCloud });
    },
    async exportToCloudOrSFTP(exportUrl, flags) {
      const { isExportToLocation } = flags;
      let exportDestination;
      if (isExportToLocation) {
        exportDestination = 'isExportToLocation';
      } else {
        exportDestination = 'isExportToCloud';
      }

      const url = `${(exportUrl || '').toString()}&${exportDestination}=true`;
      if (url && url !== '' && !url.match(/NaN/) && !url.match(/\/$/)) {
        this.overlay = true;
        let response;
        try {
          response = await fetch(url);
        } catch (err) {
          console.error(err);
        }
        if (response && response.status === 200) {
          this.flashSuccess(`${this.exportProfileName} Successfully exported to the specified location`);
        } else {
          this.flashError(`${this.exportProfileName} facing some issue while exporting`);
        }
        this.overlay = false;
      }
    },
    exportUrl(params) {
      const { isExportToLocation, isExportToCloud } = params;
      const paramsArr = [...this.inputsInternal];

      let exportType;
      if (isExportToLocation || isExportToCloud) {
        exportType = '|BASE_URL|/transmap/exportCsvAndFixedwidth?';
      } else {
        exportType = '|BASE_URL|/transmap/export?';
      }
      const url = `${exportType}fileHeaders=${this.fileHeaders}&id=${this.mongoId}&fileName=${this.fileName}&collectionName=${this.collectionName}&`;

      let urlParams;
      const objWithFY = this.searchObjectInsideArr(paramsArr, 'fiscalYear', 'type');
      const fiscalYearValue = objWithFY ? objWithFY.value : toFiscalYear(new Date());

      const showFromToDatesObj = this.searchObjectInsideArr(paramsArr, 'showFromToDates', 'type');
      const monthAndYearObj = this.searchObjectInsideArr(paramsArr, 'monthAndYear', 'type');

      if (showFromToDatesObj) {
        if (this.isExportForEntireFyWithFromToDates && this.considerFromAndToDates) {
          const { fromDate } = showFromToDatesObj;
          const { toDate } = showFromToDatesObj;
          urlParams = `fromDate=${fromDate}&toDate=${toDate}&entireFiscalYear=${fiscalYearValue}&considerFromAndToDates=${this.considerFromAndToDates}&`;
        } else if (this.isExportForEntireFyWithFromToDates) {
          urlParams = `entireFiscalYear=${fiscalYearValue}&`;
        } else {
          const { fromDate } = showFromToDatesObj;
          const { toDate } = showFromToDatesObj;
          urlParams = `fromDate=${fromDate}&toDate=${toDate}&fiscalYear=${fiscalYearValue}&`;
        }
      } else if (monthAndYearObj) {
        if (this.isExportForEntireFy) {
          urlParams = `entireFiscalYear=${fiscalYearValue}&`;
        } else {
          const { selectedMonth } = monthAndYearObj;
          const { selectedYear } = monthAndYearObj;
          urlParams = `selectedMonth=${selectedMonth}&selectedYear=${selectedYear}&fiscalYear=${fiscalYearValue}&`;
        }
      } else {
        // build url params, include optional date only when enabled
        urlParams = paramsArr.reduce((a, b) => {
          if (b.type === 'optionalDate') {
            if (b.enabled && b.value) {
              return `${a}toDate=${b.value}&`;
            }
            return a;
          }
          return `${a + b.prop}=${b.value}&`;
        }, '');
      }

      let finalUrl = url + urlParams;
      if (!checkStringEmptiness(this.program)) {
        finalUrl = `${finalUrl}program=${this.program}&`;
      }
      if (!checkStringEmptiness(this.model)) {
        finalUrl = `${finalUrl}model=${this.model}&`;
      }
      const subsUrl = (finalUrl || '')
        .replace('|BASE_URL|', backendRest.defaults.baseURL);
      return `${subsUrl}token=${this.token}`;
    },
    validateOptionalEndDateAgainstFy() {
      // find fiscal year input
      const objWithFY = this.searchObjectInsideArr(this.inputsInternal, 'fiscalYear', 'type');
      const fiscalYearValue = objWithFY ? objWithFY.value : null;
      if (!fiscalYearValue) return null;
      // compute fy start and end (FY N => from (N-1)-07-01 to N-06-30)
      const fyStart = new Date(`${fiscalYearValue - 1}-07-01`);
      const fyEnd = new Date(`${fiscalYearValue}-06-30`);
      // find optionalDate inputs
      const optionalDateObj = this.searchObjectInsideArr(this.inputsInternal, 'optionalDate', 'type');
      if (optionalDateObj && optionalDateObj.enabled && optionalDateObj.value) {
        const entered = new Date(optionalDateObj.value);
        if (entered < fyStart || entered > fyEnd) {
          return 'Date entered must fall within the fiscal year selected.';
        }
      }
      // also validate showFromToDates toDate if present and considerFromAndToDates unchecked
      const showFromToDatesObj = this.searchObjectInsideArr(this.inputsInternal, 'showFromToDates', 'type');
      if (showFromToDatesObj && showFromToDatesObj.toDate) {
        const enteredTo = new Date(showFromToDatesObj.toDate);
        if (enteredTo < fyStart || enteredTo > fyEnd) {
          return 'Date entered must fall within the fiscal year selected.';
        }
      }
      return null;
    },
  },
};

</script>
